/** Which empty state the Workflows grid shows, if any. */
export type TableEmptyState = 'no-nodes' | 'no-search-results';

export interface TableEmptyStateInput {
  /**
   * How many rows the grid will actually paint — `getVisibleRows().length`,
   * which already accounts for a collapsed group and for an active search.
   * Deliberately NOT `nodeCounts.totalNodes`: that one excludes group headers,
   * so a solution holding only empty groups would read as having no nodes
   * while the user is looking at rows.
   */
  renderedRowCount: number;
  /** Whether a search query is narrowing the grid. */
  isFiltered: boolean;
  /** Whether rows are still streaming in (bulk paste, CSV import). */
  hasPendingRowsToRender: boolean;
  /**
   * `nodeCounts.totalNodes`. A search over a solution that holds no nodes
   * must not claim that clearing the search will reveal any: that sentence
   * belongs to `no-search-results`, and it is false when the total is zero.
   */
  solutionNodeCount: number;
  /**
   * A delete hides its rows immediately and compacts them in a later idle
   * callback, which is also when a blank starter row is inserted. While that
   * window is the only reason the grid paints nothing, an empty state would
   * flash "No nodes yet" over a delete that has not finished.
   */
  hasPendingDeletion: boolean;
}

/**
 * PSTUDIO-4241 (WCAG 2.1 AA, 4.1.3 Status Messages).
 *
 * An empty grid used to be indistinguishable from a broken one: the table
 * rendered its header row and stopped, and the only text saying otherwise was
 * the toolbar's "0 Nodes", which has no programmatic relationship to the table.
 *
 * Two distinct answers, because one would have to lie in the other's case: with
 * a query in the search box, "this solution has no nodes yet" is a false claim
 * about a solution that may hold thousands.
 *
 * Silent while rows are still arriving, and silent while a delete has hidden
 * every visible row but not yet compacted them — an empty state in either
 * window is a second way for a working table to look broken.
 */
export function resolveTableEmptyState({
  renderedRowCount,
  isFiltered,
  hasPendingRowsToRender,
  solutionNodeCount,
  hasPendingDeletion,
}: TableEmptyStateInput): TableEmptyState | null {
  if (renderedRowCount > 0) return null;
  if (hasPendingRowsToRender) return null;
  if (hasPendingDeletion) return null;

  // A query in the box is only "nothing matched" when the solution actually
  // holds nodes. Otherwise clearing the search reveals the same empty grid.
  if (isFiltered && solutionNodeCount > 0) return 'no-search-results';

  return 'no-nodes';
}
